/**
 * MAGTEK UNIGATE · <location> — the location card on a SUNMI register's
 * Terminal tab (shown only when the model's policy enables a MagTek reader).
 * One status line; "Use own credentials" (write-only form) for a merchant on its
 * own UniGate account; "Use platform instead" clears it back to the platform default.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { KeyRound } from 'lucide-react';
import { api } from '../../../services/apiService';
import { toastService } from '../../../services/toastService';
import { UnigateCredentialsModal, ConfirmLight } from './UnigateCredentialsModal';
import { unigateLine, PAYMENT_PROCESSORS_PATH } from './unigateCreds';

/** The location's UniGate status ({source, set}); undefined = loading, null = failed. */
export function useLocationUnigate(tenantId: string, locationId: string) {
  const [status, setStatus] = useState<any | null | undefined>(undefined);
  const reload = useCallback(() => {
    let live = true;
    setStatus(undefined);
    api.posNmi.getLocationMagtekCreds(tenantId, locationId)
      .then((s: any) => { if (live) setStatus(s || null); })
      .catch(() => { if (live) setStatus(null); });
    return () => { live = false; };
  }, [tenantId, locationId]);
  useEffect(() => reload(), [reload]);
  return { status, reload };
}

/** The one line — with a link to the platform settings when platform credentials are missing. */
export const UnigateLine: React.FC<{ status: any }> = ({ status }) => {
  const l = unigateLine(status);
  return (
    <span data-testid="ug-line" className="inline-flex items-center gap-2 text-[12.5px]">
      <span className={`h-2 w-2 shrink-0 rounded-full ${l.tone === 'ok' ? 'bg-emerald-500' : l.tone === 'warn' ? 'bg-ruby' : 'bg-stone-300'}`} />
      {l.linkToSettings
        ? <Link to={PAYMENT_PROCESSORS_PATH} className="font-medium text-ruby-deep underline-offset-2 hover:underline">{l.text}</Link>
        : <span className={l.tone === 'warn' ? 'font-medium text-ruby-deep' : 'text-stone-700'}>{l.text}</span>}
    </span>
  );
};

export const MagtekLocationCard: React.FC<{ tenantId: string; locationId: string; locationName: string }> = ({ tenantId, locationId, locationName }) => {
  const { status, reload } = useLocationUnigate(tenantId, locationId);
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const own = status?.source === 'location';
  const btn = 'min-h-[44px] rounded-lg px-3 text-xs font-semibold text-ruby hover:bg-[#FFF1F3]';

  const usePlatform = async () => {
    setBusy(true);
    try {
      await api.posNmi.deleteLocationMagtekCreds(tenantId, locationId);
      toastService.success('This location now uses the platform UniGate credentials.');
      setConfirming(false);
      reload();
    } catch (e: any) {
      toastService.error(e?.serverMessage || e?.message || 'Could not switch to the platform credentials.');
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white" data-testid="mt-location-card">
      <div className="flex flex-wrap items-center gap-3 px-[18px] py-3">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[#FFF1F3] text-ruby"><KeyRound size={16} /></span>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-stone-900">MagTek UniGate · {locationName}</div>
          <UnigateLine status={status} />
          {status && <div data-testid="mt-te-line" className="text-[12.5px] text-stone-700">Website key: {status.teconnect?.publicKey ? `Own · ${status.teconnect.environment === 'production' ? 'Production' : 'Sandbox'}` : 'not set'}</div>}
        </div>
        <button type="button" className={btn} onClick={() => setEditing(true)} data-testid="mt-use-own">
          {own ? 'Replace own credentials' : 'Use own credentials'}
        </button>
        {own && <button type="button" className={btn} onClick={() => setConfirming(true)} data-testid="mt-use-platform">Use platform instead</button>}
      </div>
      {editing && (
        <UnigateCredentialsModal title="Own UniGate credentials" scope={locationName} scopeIsLocation
          withTeconnect teconnect={status?.teconnect} teconnectHint="Used only when this location uses its own MagTek credentials."
          onSave={async (body) => {
            await api.posNmi.putLocationMagtekCreds(tenantId, locationId, body);
            toastService.success('Own UniGate credentials saved for this location.');
            reload();
          }}
          onClose={() => setEditing(false)} />
      )}
      {confirming && (
        <ConfirmLight title="Use platform credentials?" confirmLabel="Use platform" busy={busy}
          message={`${locationName}'s own UniGate credentials will be deleted. Its MagTek readers will authorise with the platform credentials.`}
          onConfirm={usePlatform} onCancel={() => setConfirming(false)} />
      )}
    </div>
  );
};

export default MagtekLocationCard;
